/**
 * VISA/CERTS: Certificate management section for helper profiles
 */

import React, { useState, useEffect } from 'react';
import { Award, Plus, Edit2, Trash2, X, Check, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import {
  CERTIFICATE_TYPES,
  CERTIFICATE_GROUPS,
  COMMON_CERTIFICATES,
  CERTIFICATE_CATEGORIES,
  getCertificateLabel,
  calculateCertificateStatus,
  getStatusBadgeClass,
  getStatusLabel
} from '../data/certificateTypes';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3002';

export default function CertificateSection({ userId, readOnly = false }) {
  const [certificates, setCertificates] = useState([]);
  const [groupedCertificates, setGroupedCertificates] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingCert, setEditingCert] = useState(null);
  const [formData, setFormData] = useState({
    certificate_type: '',
    certificate_category: '',
    issuing_authority: '',
    certificate_number: '',
    issue_date: '',
    expiry_date: ''
  });
  const [saving, setSaving] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});

  // Fetch certificates on mount
  useEffect(() => {
    fetchCertificates();
  }, []);

  const fetchCertificates = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('dockmate_token');
      const response = await fetch(`${API_URL}/crew/certificates`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setCertificates(data.data.certificates || []);
        setGroupedCertificates(data.data.grouped_certificates || {});
        // Auto-expand categories that have certificates
        const expanded = {};
        CERTIFICATE_CATEGORIES.forEach(cat => {
          if ((data.data.grouped_certificates?.[cat] || []).length > 0) {
            expanded[cat] = true;
          }
        });
        setExpandedCategories(expanded);
      } else {
        setError(data.error?.message || 'Failed to fetch certificates');
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.certificate_type) return;

    try {
      setSaving(true);
      const token = localStorage.getItem('dockmate_token');
      const url = editingCert
        ? `${API_URL}/crew/certificates/${editingCert.id}`
        : `${API_URL}/crew/certificates`;
      const method = editingCert ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (data.success) {
        await fetchCertificates();
        resetForm();
      } else {
        setError(data.error?.message || 'Failed to save certificate');
      }
    } catch (err) {
      setError('Failed to save certificate');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (certId) => {
    if (!window.confirm('Delete this certificate?')) return;

    try {
      const token = localStorage.getItem('dockmate_token');
      const response = await fetch(`${API_URL}/crew/certificates/${certId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        await fetchCertificates();
      } else {
        setError(data.error?.message || 'Failed to delete certificate');
      }
    } catch (err) {
      setError('Failed to delete certificate');
    }
  };

  const handleEdit = (cert) => {
    setEditingCert(cert);
    setFormData({
      certificate_type: cert.certificate_type,
      certificate_category: cert.certificate_category,
      issuing_authority: cert.issuing_authority || '',
      certificate_number: cert.certificate_number || '',
      issue_date: cert.issue_date || '',
      expiry_date: cert.expiry_date || ''
    });
    setShowForm(true);
  };

  const handleQuickAdd = (certCode) => {
    const certType = CERTIFICATE_TYPES[certCode];
    setEditingCert(null);
    setFormData({
      certificate_type: certCode,
      certificate_category: certType?.category || '',
      issuing_authority: '',
      certificate_number: '',
      issue_date: '',
      expiry_date: ''
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingCert(null);
    setFormData({
      certificate_type: '',
      certificate_category: '',
      issuing_authority: '',
      certificate_number: '',
      issue_date: '',
      expiry_date: ''
    });
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Get selected category's certificates for dropdown
  const getCategoryOptions = () => {
    if (!formData.certificate_category) return [];
    return CERTIFICATE_GROUPS.find(g => g.label === formData.certificate_category)?.certificates || [];
  };

  // Count total certificates
  const totalCerts = certificates.length;
  const expiringCount = certificates.filter(c =>
    c.calculated_status === 'renewal_needed' || c.calculated_status === 'expired'
  ).length;

  if (loading) {
    return (
      <div className="card">
        <div className="flex items-center justify-center py-8">
          <span className="spinner h-6 w-6" />
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-display font-semibold text-navy-900">
          <Award className="h-5 w-5 inline mr-2" />
          Certificates & Qualifications
        </h2>
        {totalCerts > 0 && (
          <span className="text-sm text-navy-500">
            {totalCerts} certificate{totalCerts !== 1 ? 's' : ''}
            {expiringCount > 0 && (
              <span className="ml-2 text-yellow-600">
                ({expiringCount} expiring)
              </span>
            )}
          </span>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 rounded-apple flex items-start">
          <AlertTriangle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" />
          <p className="text-red-700 text-sm">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Quick add buttons */}
      {!readOnly && !showForm && (
        <div className="flex flex-wrap gap-2 mb-4">
          {COMMON_CERTIFICATES.filter(c => !certificates.find(cert => cert.certificate_type === c)).map(certCode => (
            <button
              key={certCode}
              onClick={() => handleQuickAdd(certCode)}
              className="inline-flex items-center px-3 py-1.5 text-sm bg-green-50 hover:bg-green-100 text-green-700 rounded-full border border-green-200 transition-colors"
            >
              <Plus className="h-3.5 w-3.5 mr-1" />
              {getCertificateLabel(certCode)}
            </button>
          ))}
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center px-3 py-1.5 text-sm bg-primary-50 hover:bg-primary-100 text-primary-700 rounded-full border border-primary-200 transition-colors"
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            Add Certificate
          </button>
        </div>
      )}

      {/* Add/Edit Form */}
      {showForm && !readOnly && (
        <form onSubmit={handleSubmit} className="mb-6 p-4 bg-navy-50 rounded-apple border border-navy-200">
          <h3 className="text-sm font-semibold text-navy-900 mb-3">
            {editingCert ? 'Edit Certificate' : 'Add New Certificate'}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-navy-700 mb-1">
                Category *
              </label>
              <select
                value={formData.certificate_category}
                onChange={(e) => setFormData({
                  ...formData,
                  certificate_category: e.target.value,
                  certificate_type: '' // Reset type when category changes
                })}
                className="input-field"
                required
              >
                <option value="">Select category...</option>
                {CERTIFICATE_CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            {/* Certificate Type */}
            <div>
              <label className="block text-sm font-medium text-navy-700 mb-1">
                Certificate *
              </label>
              <select
                value={formData.certificate_type}
                onChange={(e) => setFormData({ ...formData, certificate_type: e.target.value })}
                className="input-field"
                required
                disabled={!formData.certificate_category}
              >
                <option value="">Select certificate...</option>
                {getCategoryOptions().map(code => (
                  <option key={code} value={code}>
                    {CERTIFICATE_TYPES[code]?.label || code}
                  </option>
                ))}
              </select>
            </div>

            {/* Show expiry info if certificate expires */}
            {formData.certificate_type && CERTIFICATE_TYPES[formData.certificate_type]?.expires && (
              <div className="md:col-span-2">
                <div className="p-2 bg-yellow-50 rounded-lg border border-yellow-200 text-xs text-yellow-700">
                  This certificate expires every {CERTIFICATE_TYPES[formData.certificate_type]?.expiryYears} year(s).
                  Please enter the expiry date below.
                </div>
              </div>
            )}

            {/* Issuing Authority */}
            <div>
              <label className="block text-sm font-medium text-navy-700 mb-1">
                Issuing Authority
              </label>
              <input
                type="text"
                value={formData.issuing_authority}
                onChange={(e) => setFormData({ ...formData, issuing_authority: e.target.value })}
                className="input-field"
                placeholder="e.g. MCA, RYA, USCG"
              />
            </div>

            {/* Certificate Number */}
            <div>
              <label className="block text-sm font-medium text-navy-700 mb-1">
                Certificate Number
              </label>
              <input
                type="text"
                value={formData.certificate_number}
                onChange={(e) => setFormData({ ...formData, certificate_number: e.target.value })}
                className="input-field"
                placeholder="Optional"
              />
            </div>

            {/* Issue Date */}
            <div>
              <label className="block text-sm font-medium text-navy-700 mb-1">
                Issue Date
              </label>
              <input
                type="date"
                value={formData.issue_date}
                onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })}
                className="input-field"
              />
            </div>

            {/* Expiry Date */}
            <div>
              <label className="block text-sm font-medium text-navy-700 mb-1">
                Expiry Date
                {formData.certificate_type && CERTIFICATE_TYPES[formData.certificate_type]?.expires && (
                  <span className="text-red-500 ml-1">*</span>
                )}
              </label>
              <input
                type="date"
                value={formData.expiry_date}
                onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                className="input-field"
                required={formData.certificate_type && CERTIFICATE_TYPES[formData.certificate_type]?.expires}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={resetForm}
              className="px-4 py-2 text-sm text-navy-600 hover:bg-navy-100 rounded-full transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !formData.certificate_type}
              className="btn-primary text-sm py-2 px-4"
            >
              {saving ? (
                <>
                  <span className="spinner h-4 w-4 mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-1" />
                  {editingCert ? 'Update' : 'Add'} Certificate
                </>
              )}
            </button>
          </div>
        </form>
      )}

      {/* Certificate List by Category */}
      {certificates.length === 0 ? (
        <p className="text-navy-500 text-sm py-4">
          No certificates added yet.
          {!readOnly && ' Click a button above to add your first certificate.'}
        </p>
      ) : (
        <div className="space-y-3">
          {CERTIFICATE_CATEGORIES.filter(cat => (groupedCertificates[cat] || []).length > 0).map(category => (
            <div key={category} className="border border-navy-200 rounded-apple overflow-hidden">
              {/* Category Header */}
              <button
                onClick={() => toggleCategory(category)}
                className="w-full flex items-center justify-between p-3 bg-navy-50 hover:bg-navy-100 transition-colors"
              >
                <span className="font-medium text-navy-900">{category}</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-navy-500">
                    {(groupedCertificates[category] || []).length}
                  </span>
                  {expandedCategories[category] ? (
                    <ChevronDown className="h-4 w-4 text-navy-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-navy-400" />
                  )}
                </div>
              </button>

              {/* Category Contents */}
              {expandedCategories[category] && (
                <div className="p-3 space-y-2">
                  {(groupedCertificates[category] || []).map(cert => {
                    const typeInfo = cert.type_info || CERTIFICATE_TYPES[cert.certificate_type] || {};
                    const status = cert.calculated_status || calculateCertificateStatus(cert.expiry_date, typeInfo.expires);

                    return (
                      <div
                        key={cert.id}
                        className={`p-3 rounded-lg ${
                          status === 'expired' ? 'bg-red-50 border border-red-200' :
                          status === 'renewal_needed' ? 'bg-yellow-50 border border-yellow-200' :
                          'bg-white border border-navy-100'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-medium text-navy-900 text-sm">
                                {typeInfo.label || cert.certificate_type}
                              </h4>
                              {typeInfo.expires && (
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(status)}`}>
                                  {getStatusLabel(status)}
                                </span>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-3 text-xs text-navy-600">
                              {cert.issuing_authority && (
                                <span>Issued by: {cert.issuing_authority}</span>
                              )}
                              {cert.certificate_number && (
                                <span>No: {cert.certificate_number}</span>
                              )}
                              {cert.issue_date && (
                                <span>Issued: {formatDate(cert.issue_date)}</span>
                              )}
                              {cert.expiry_date && (
                                <span className={status === 'expired' ? 'text-red-600 font-medium' : ''}>
                                  Expires: {formatDate(cert.expiry_date)}
                                </span>
                              )}
                              {!typeInfo.expires && !cert.expiry_date && (
                                <span className="text-green-600">No expiry</span>
                              )}
                            </div>
                          </div>

                          {!readOnly && (
                            <div className="flex items-center gap-1 ml-3">
                              <button
                                onClick={() => handleEdit(cert)}
                                className="p-1.5 text-navy-500 hover:text-navy-700 hover:bg-navy-100 rounded-lg transition-colors"
                                title="Edit"
                              >
                                <Edit2 className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={() => handleDelete(cert.id)}
                                className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
